import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { QuoteLineItem } from '@/types/quote';
// Line item functions are now passed as props from parent
import { Plus, Trash2, ChevronDown, Copy, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface QuoteLineItemsEditorProps {
  quoteId: string;
  lineItems: QuoteLineItem[];
  pricingMode: 'inclusive' | 'exclusive';
  addLineItem: (item: Omit<QuoteLineItem, 'id' | 'created_at' | 'updated_at' | 'subtotal' | 'vat_amount' | 'total'>) => Promise<void>;
  updateLineItem: (itemId: string, updates: Partial<QuoteLineItem>) => Promise<void>;
  deleteLineItem: (itemId: string) => Promise<void>;
  duplicateLineItem: (itemId: string) => Promise<void>;
  reorderLineItems: (items: QuoteLineItem[]) => Promise<void>;
}

// Preset line items for quick adding
// Dutch number formatting helper
const formatNL = (amount: number): string => {
  return new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const PRESET_ITEMS = [
  { label: 'Consultancy uur', description: 'Consultancy werkzaamheden', quantity: 1, unit_price: 125, vat_rate: 21 },
  { label: 'Projectmanagement', description: 'Projectmanagement', quantity: 1, unit_price: 95, vat_rate: 21 },
  { label: 'Senior developer', description: 'Senior development werkzaamheden', quantity: 1, unit_price: 115, vat_rate: 21 },
  { label: 'Junior developer', description: 'Junior development werkzaamheden', quantity: 1, unit_price: 85, vat_rate: 21 },
  { label: 'Kilometervergoeding', description: 'Reiskosten per km', quantity: 1, unit_price: 0.23, vat_rate: 21 },
  { label: 'Training (dagdeel)', description: 'Training/workshop dagdeel', quantity: 1, unit_price: 650, vat_rate: 21 },
  { label: 'Licentie (maand)', description: 'Software licentie per maand', quantity: 12, unit_price: 49, vat_rate: 21 },
  { label: 'Support (jaar)', description: 'Jaarlijks support contract', quantity: 1, unit_price: 1200, vat_rate: 21 },
];

// Sortable row component
const SortableRow = ({ 
  item, 
  onFieldChange, 
  onFieldBlur, 
  onDelete, 
  onDuplicate,
  pricingMode
}: { 
  item: QuoteLineItem;
  onFieldChange: (itemId: string, field: string, value: any) => void;
  onFieldBlur: (itemId: string, field: string, value: any) => void;
  onDelete: (itemId: string) => void;
  onDuplicate: (itemId: string) => void;
  pricingMode: 'inclusive' | 'exclusive';
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? 'bg-muted' : ''}>
      <TableCell className="w-8">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab hover:bg-muted p-1 rounded touch-none"
          aria-label="Versleep om te herschikken"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      <TableCell>
        <Input
          value={item.description}
          onChange={(e) => onFieldChange(item.id, 'description', e.target.value)}
          onBlur={(e) => onFieldBlur(item.id, 'description', e.target.value)}
          className="min-w-[200px]"
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          step="0.01"
          value={item.quantity}
          onChange={(e) => onFieldChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
          onBlur={(e) => onFieldBlur(item.id, 'quantity', parseFloat(e.target.value) || 0)}
          className="w-20"
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          step="0.01"
          value={item.unit_price}
          onChange={(e) => onFieldChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
          onBlur={(e) => onFieldBlur(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
          className="w-24"
        />
      </TableCell>
      {pricingMode === 'inclusive' && (
        <TableCell>
          <Select
            value={item.vat_rate.toString()}
            onValueChange={(value) => {
              const newRate = parseInt(value);
              onFieldChange(item.id, 'vat_rate', newRate);
              onFieldBlur(item.id, 'vat_rate', newRate);
            }}
          >
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0%</SelectItem>
              <SelectItem value="9">9%</SelectItem>
              <SelectItem value="21">21%</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
      )}
      <TableCell className="text-right font-medium">
        €{formatNL(item.total)}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDuplicate(item.id)}
            title="Dupliceer regel"
          >
            <Copy className="h-4 w-4 text-muted-foreground" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(item.id)}
            title="Verwijder regel"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export const QuoteLineItemsEditor = ({ 
  quoteId, 
  lineItems,
  pricingMode,
  addLineItem,
  updateLineItem,
  deleteLineItem,
  duplicateLineItem,
  reorderLineItems
}: QuoteLineItemsEditorProps) => {
  const [localItems, setLocalItems] = useState<QuoteLineItem[]>(lineItems);
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync with prop changes
  useEffect(() => {
    setLocalItems(lineItems);
  }, [lineItems]);

  const calculateItemTotals = (quantity: number, unitPrice: number, vatRate: number) => {
    if (pricingMode === 'exclusive') {
      // Prijs is excl. BTW - geen BTW berekening
      const total = quantity * unitPrice;
      return { subtotal: total, vatAmount: 0, total };
    }
    // Prijs is inclusief BTW - bereken BTW terug
    const total = quantity * unitPrice;
    const vatAmount = total * (vatRate / (100 + vatRate));
    const subtotal = total - vatAmount;
    return { subtotal, vatAmount, total };
  };

  const handleFieldChange = (itemId: string, field: string, value: any) => {
    // 1. Optimistic update
    setLocalItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      
      const updated = { ...item, [field]: value };
      
      // Recalculate totals if quantity, unit_price or vat_rate changes
      if (['quantity', 'unit_price', 'vat_rate'].includes(field)) {
        const totals = calculateItemTotals(
          field === 'quantity' ? value : updated.quantity,
          field === 'unit_price' ? value : updated.unit_price,
          field === 'vat_rate' ? value : updated.vat_rate
        );
        return { ...updated, subtotal: totals.subtotal, vat_amount: totals.vatAmount, total: totals.total };
      }
      
      return updated;
    }));

    // 2. Debounced save (500ms)
    if (debounceTimers.current[itemId]) {
      clearTimeout(debounceTimers.current[itemId]);
    }
    
    debounceTimers.current[itemId] = setTimeout(() => {
      updateLineItem(itemId, { [field]: value });
    }, 500);
  };

  const handleFieldBlur = (itemId: string, field: string, value: any) => {
    // Clear debounce timer and save immediately
    if (debounceTimers.current[itemId]) {
      clearTimeout(debounceTimers.current[itemId]);
    }
    updateLineItem(itemId, { [field]: value });
  };

  const handleAddItem = async () => {
    await addLineItem({
      quote_id: quoteId,
      position: localItems.length,
      description: 'Nieuwe regel',
      quantity: 1,
      unit_price: 0,
      vat_rate: 21,
    });
  };

  const handleAddPresetItem = async (preset: typeof PRESET_ITEMS[0]) => {
    await addLineItem({
      quote_id: quoteId,
      position: localItems.length,
      description: preset.description,
      quantity: preset.quantity,
      unit_price: preset.unit_price,
      vat_rate: preset.vat_rate,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localItems.findIndex((item) => item.id === active.id);
      const newIndex = localItems.findIndex((item) => item.id === over.id);
      
      const reordered = arrayMove(localItems, oldIndex, newIndex);
      setLocalItems(reordered);
      reorderLineItems(reordered);
    }
  };

  // Calculate VAT breakdown
  const vatBreakdown = localItems.reduce((acc, item) => {
    const rate = item.vat_rate;
    if (!acc[rate]) acc[rate] = { subtotal: 0, vat: 0, total: 0 };
    acc[rate].subtotal += item.subtotal;
    acc[rate].vat += item.vat_amount;
    acc[rate].total += item.total;
    return acc;
  }, {} as Record<number, { subtotal: number; vat: number; total: number }>);

  const grandTotal = localItems.reduce((sum, item) => sum + item.total, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Regels</CardTitle>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Presets
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Snelle regeltoevoegen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PRESET_ITEMS.map((preset, index) => (
                  <DropdownMenuItem
                    key={index}
                    onClick={() => handleAddPresetItem(preset)}
                  >
                    <div className="flex justify-between w-full">
                      <span>{preset.label}</span>
                      <span className="text-muted-foreground">€{formatNL(preset.unit_price)}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={handleAddItem} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Regel toevoegen
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {localItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>Nog geen regels toegevoegd</p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    Kies een preset
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  {PRESET_ITEMS.map((preset, index) => (
                    <DropdownMenuItem
                      key={index}
                      onClick={() => handleAddPresetItem(preset)}
                    >
                      <div className="flex justify-between w-full">
                        <span>{preset.label}</span>
                        <span className="text-muted-foreground">€{formatNL(preset.unit_price)}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={handleAddItem} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Lege regel
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className={pricingMode === 'exclusive' ? "w-[45%]" : "w-[38%]"}>Omschrijving</TableHead>
                    <TableHead className="w-[12%]">Aantal</TableHead>
                    <TableHead className="w-[15%]">{pricingMode === 'exclusive' ? 'Prijs (excl. BTW)' : 'Prijs (incl. BTW)'}</TableHead>
                    {pricingMode === 'inclusive' && <TableHead className="w-[10%]">BTW%</TableHead>}
                    <TableHead className="w-[12%] text-right">Totaal</TableHead>
                    <TableHead className="w-[5%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext
                    items={localItems.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {localItems.map((item) => (
                      <SortableRow
                        key={item.id}
                        item={item}
                        onFieldChange={handleFieldChange}
                        onFieldBlur={handleFieldBlur}
                        onDelete={deleteLineItem}
                        onDuplicate={duplicateLineItem}
                        pricingMode={pricingMode}
                      />
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </DndContext>

            {/* VAT Breakdown - only show for inclusive pricing */}
            {pricingMode === 'inclusive' ? (
              <div className="mt-6 space-y-2 max-w-md ml-auto">
                <div className="text-sm font-medium text-muted-foreground">BTW Overzicht</div>
                {Object.entries(vatBreakdown).map(([rate, amounts]) => (
                  <div key={rate} className="flex justify-between text-sm">
                    <span>BTW {rate}% (uit €{formatNL(amounts.total)})</span>
                    <span>€{formatNL(amounts.vat)}</span>
                  </div>
                ))}
                <Separator className="my-2" />
                <div className="flex justify-between font-bold">
                  <span>Totaal incl. BTW</span>
                  <span>€{formatNL(grandTotal)}</span>
                </div>
              </div>
            ) : (
              <div className="mt-6 space-y-2 max-w-md ml-auto">
                <Separator className="my-2" />
                <div className="flex justify-between font-bold text-primary">
                  <span>Totaal excl. BTW</span>
                  <span>€{formatNL(grandTotal)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
